import React from 'react';
import { FubDealsIntegrationToggleWrapper } from 'feature-toggle';
import { AccessChecker } from 'authorization-scope';
import {
  ConnectionProfile,
  ExternalProjectLink,
  Project,
  PROJECT_LINKS_UPDATED,
  PROJECT_SYNCING_UPDATED,
  SetupStatus,
  TeamPermission,
  useProjectsService
} from 'nekst-api';
import { Separator } from 'features/nekst-widgets';
import { FubConnectionContext, useFubConnectionContextRequired } from '../../../Integrations';
import {
  Button,
  ButtonColor,
  ButtonSize,
  ButtonVariant,
  Header4,
  LoadingAnimation,
  PopupContext,
  SimpleBlock,
  Text,
  TextLink,
  usePopupManager
} from 'ui-builder';
import FubConnectionBlock from '../../../People/View/Connections/FubConnectionBlock';
import { LinkStatusWidget } from '../../../People/View/Connections/LinkStatus';
import { useDataLoaderNew } from 'data-loader';
import CreateExternalProjectForm from './CreateExternalProjectForm';
import SetupNotCompletedPopup from './SetupNotCompletedPopup';
import useUnlinkProjectOption from './unlinkProjectAction';
import styles from './ExternalLinksList.module.scss';

function NoFubConnectionView() {
  return (
    <Text>
      To integrate your contacts with other applications, see our current integration partners
      {' '}
      <TextLink href="/settings/integrations/fub">here</TextLink>.
    </Text>
  );
}

function NoExistingLinksView(props: {
  project: Project,
  profile: ConnectionProfile,
}) {

  const popupManager = usePopupManager();

  return (
    <FubConnectionBlock
      content={null}
      actions={(
        <Button
          text="Create FUB Deal"
          variant={ButtonVariant.FILLED}
          size={ButtonSize.SMALL}
          onClick={() => {
            if (props.profile.projectsSyncSetupStatuses[props.project.type] === SetupStatus.SETUP) {
              popupManager.openPopup(<CreateExternalProjectForm projectId={props.project.id} />);
            } else {
              popupManager.openPopup(
                <SetupNotCompletedPopup
                  profile={props.profile}
                  projectType={props.project.type}
                />
              );
            }
          }}
        />
      )}
    />
  );
}

function LinkStatus(props: { link: ExternalProjectLink }) {
  return (
    <LinkStatusWidget
      isConflicting={props.link.isConflicting}
      conflictingTitle={`Transaction syncing is currently disabled due to a conflict. Nekst couldn't determine how to sync two unsynced records.`}
    />
  );
}

function UnlinkDealButton(props: {
  project: Project,
  link: ExternalProjectLink,
}) {
  const unlinkProjectOption = useUnlinkProjectOption(props.project.id, props.link.id);

  return (
    <Button
      text="Unlink"
      size={ButtonSize.SMALL}
      color={ButtonColor.RED}
      variant={ButtonVariant.FILLED}
      onClick={async () => {
        await unlinkProjectOption.onSelected();
      }}
    />
  );
}

function LinkActionsButton(props: {
  project: Project,
  link: ExternalProjectLink,
}) {
  return (
    <UnlinkDealButton project={props.project} link={props.link} />
  );
}

function ExistingLinksList(
  props: {
    project: Project,
  }
) {
  const getConnectionLabel = (projectLink: ExternalProjectLink) => {
    return 'Open Deal in FUB';
  };

  return (
    <>
      {props.project.externalLinks.map((link) => (
        <FubConnectionBlock
          content={(
            <SimpleBlock className={styles.nameWithStatus}>
              <LinkStatus link={link} />
              <TextLink href={link._externalProjectUrl} target="_blank">
                {getConnectionLabel(link)}
              </TextLink>
            </SimpleBlock>
          )}
          actions={(
            <LinkActionsButton project={props.project} link={link} />
          )}
        />
      ))}
    </>
  );
}

function ExternalLinksListView(props: {
  project: Project
}) {
  const context = useFubConnectionContextRequired();

  let content;

  if (!context.loaded) {
    content = (<LoadingAnimation />);
  } else if (context.profiles.length === 0) {
    content = (<NoFubConnectionView />);
  } else if (props.project.externalLinks.length) {
    content = <ExistingLinksList project={props.project} />;
  } else {
    const profile = context.profiles[0];

    content = <NoExistingLinksView project={props.project} profile={profile} />;
  }

  return (
    <SimpleBlock>
      <Header4>Integrations</Header4>
      {content}
    </SimpleBlock>
  );
}

function ExternalLinksList(props: {
  projectId: number,
}) {
  const projectService = useProjectsService();

  const { data: project } = useDataLoaderNew(
    async () => {
      return await projectService.getById(props.projectId);
    },
    {
      dependencies: [props.projectId],
      events: [PROJECT_SYNCING_UPDATED, PROJECT_LINKS_UPDATED]
    }
  );

  if (project) {
    return (
      <FubConnectionContext>
        <PopupContext>
          <ExternalLinksListView project={project} />
        </PopupContext>
      </FubConnectionContext>
    );
  } else {
    return (
      <LoadingAnimation />
    );
  }
}

export function ProjectExternalLinksBlock(props: {
  projectId: number,
}) {
  return (
    <FubDealsIntegrationToggleWrapper>
      <AccessChecker permissionToCheck={TeamPermission.READ_FUB_CONNECTION}>
        <Separator />
        <ExternalLinksList projectId={props.projectId} />
      </AccessChecker>
    </FubDealsIntegrationToggleWrapper>
  );
}
